'use client';

import { DateTime } from 'luxon';
import { Fragment, useMemo, useState } from 'react';

import {
  SegmentedControl,
  SegmentedControlItem,
} from '../../../core-components/SegmentedControl';
import { TextButton } from '../../../core-components/TextButton';
import { DataRow } from '../../shared/DataRow';
import { Dialog, DialogContent } from '../../shared/Dialog';
import { FlightTimelineEntry } from '../../shared/FlightTimelineEntry';
import {
  FlightLegDetails
} from '../FlightLegDetails/FlightLegDetails';

import type { I18NInterface } from '../../../utils/i18n';
import type {
  FlightLegDetailsProps} from '../FlightLegDetails/FlightLegDetails';
import type { Locale } from '@tuic/i18n';
import type { FC} from 'react';

type I18N = I18NInterface<
  'details' | 'changeOver' | 'durationFormat' | 'arrival' | 'departure'
>;

export type FlightOverviewProps = {
  arrivalLegs?: Array<FlightOverviewLeg>;
  departureLegs?: Array<FlightOverviewLeg>;

  i18n: I18N;
  locale: Locale;

  onDetailsOpenChange?: (isOpen: boolean, leg: FlightOverviewLeg) => void;
};

export type FlightOverviewLeg = {
  departureDate: string;
  departureAirport: string;

  arrivalDate: string;
  arrivalAirport: string;

  details?: FlightLegDetailsProps;
};

export const FlightOverview: FC<FlightOverviewProps> = ({
  arrivalLegs,
  departureLegs,
  i18n,
  locale,
  onDetailsOpenChange,
}) => {
  const [selectedTab, setSelectedTab] = useState<'arrival' | 'departure'>(
    'arrival',
  );

  const legs = (selectedTab === 'arrival' ? arrivalLegs : departureLegs) ?? [];

  if (legs.length === 0) return null;

  const startDate = DateTime.fromISO(legs[0].departureDate);
  const headline = startDate.toLocaleString(
    {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
    { locale },
  );

  return (
    <div className="flex flex-col text-marine-high-emphasis gap-8">
      <SegmentedControl
        value={selectedTab}
        onValueChange={tab => setSelectedTab(tab as 'arrival' | 'departure')}
      >
        <SegmentedControlItem value="arrival" label={i18n.arrival} />
        <SegmentedControlItem value="departure" label={i18n.departure} />
      </SegmentedControl>

      <div className="flex flex-col gap-4">
        {headline && <h3 className="headline-xl">{headline}</h3>}

        {legs.map((leg, index, array) => (
          <Fragment key={index}>
            <FlightLeg
              key={index}
              startDate={startDate}
              leg={leg}
              i18n={i18n}
              locale={locale}
              onDetailsOpenChange={isOpen => onDetailsOpenChange?.(isOpen, leg)}
            />
            {index < array.length - 1 && (
              <ChangeOver
                arrivalTime={leg.arrivalDate}
                departureTime={array[index + 1].departureDate}
                i18n={i18n}
              />
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
};

const FlightLeg: FC<{
  startDate: DateTime;
  leg: FlightOverviewLeg;
  i18n: I18N;
  locale: Locale;
  onDetailsOpenChange?: (isOpen: boolean) => void;
}> = ({ leg, i18n, locale, startDate, onDetailsOpenChange }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const startDay = useMemo(() => startDate.startOf('day'), [startDate]);
  const departureDate = useMemo(
    () => DateTime.fromISO(leg.departureDate),
    [leg.departureDate],
  );
  const arrivalDate = useMemo(
    () => DateTime.fromISO(leg.arrivalDate),
    [leg.arrivalDate],
  );

  const departureTimeDayShift = useMemo(
    () => departureDate.startOf('day').diff(startDay, 'days').as('days'),
    [departureDate, startDay],
  );
  const arrivalTimeDayShift = useMemo(
    () => arrivalDate.startOf('day').diff(startDay, 'days').as('days'),
    [arrivalDate, startDay],
  );
  const flightDuration = useMemo(
    () => arrivalDate.diff(departureDate, ['hours', 'minutes']),
    [arrivalDate, departureDate],
  );

  const handleDetailsOpenChange = (isOpen: boolean) => {
    setDetailsOpen(isOpen);
    onDetailsOpenChange?.(isOpen);
  };

  return (
    <>
      <FlightTimelineEntry
        departureTime={departureDate.toLocaleString(
          { timeStyle: 'short' },
          { locale },
        )}
        departureAirport={leg.departureAirport}
        departureTimeDayShift={departureTimeDayShift}
        arrivalTime={arrivalDate.toLocaleString(
          { timeStyle: 'short' },
          { locale },
        )}
        arrivalAirport={leg.arrivalAirport}
        arrivalTimeDayShift={arrivalTimeDayShift}
        rowItems={[
          {
            icon: 'compass',
            value: flightDuration.toFormat(i18n.durationFormat),
            label: leg.details ? (
              <TextButton
                onClick={() => handleDetailsOpenChange(true)}
                iconRight="nav-arrow-right"
              >
                {i18n.details}
              </TextButton>
            ) : undefined,
          },
        ]}
      />
      {leg.details && (
        <Dialog open={detailsOpen} onOpenChange={handleDetailsOpenChange}>
          <DialogContent disableSpacing>
            <FlightLegDetails
              {...leg.details}
              onBack={() => {
                handleDetailsOpenChange(false);
                leg.details?.onBack?.();
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const ChangeOver: FC<{
  arrivalTime: string;
  departureTime: string;
  i18n: I18N;
}> = ({ arrivalTime, departureTime, i18n }) => {
  const duration = DateTime.fromISO(departureTime).diff(
    DateTime.fromISO(arrivalTime),
    ['hours', 'minutes'],
  );

  return (
    <DataRow
      value={duration.toFormat(i18n.durationFormat)}
      label={i18n.changeOver}
      icon="clock"
      className="mb-4"
      iconSize="sm"
    />
  );
};
